// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { BurnMintFactory } from '@daml.js/splice-api-token-burn-mint-v1-1.0.0/lib/Splice/Api/Token/BurnMintV1';
import { ContextContractIds } from '@daml.js/utility-bridge-v0/lib/Utility/Bridge/V0/ApiUtil/module';
import { ContractId, DisclosedContract } from '@daml/types';
import { MintOutput, RegistryApi } from '@digitalasset/utility-api-ts-client';
import { AnyContract } from '@daml.js/splice-api-token-metadata/lib/Splice/Api/Token/MetadataV1';
import { INSTRUMENT_IDENTIFIER } from '../../../utils/constants';

export type RequiredContracts = {
  factoryCid: ContractId<BurnMintFactory>;
  contextContractIds: ContextContractIds;
  requiredDisclosedContracts: DisclosedContract[];
};

const extractContractId = (values: any, key: string): string | undefined => {
  const entry = values[key];
  if (entry?.tag === 'AV_ContractId' && entry?.value) {
    return entry.value;
  }
  return undefined;
};

const getContextContracts = async (
  utilityRegistryApi: RegistryApi,
  crossChainRepresentative: string,
  inputHoldingCids: Array<string>,
  outputs: Array<MintOutput>
): Promise<RequiredContracts> => {
  const request = {
    instrumentId: {
      id: INSTRUMENT_IDENTIFIER,
      admin: crossChainRepresentative,
    },
    inputHoldingCids,
    outputs,
  };

  const factoryWithContext = await utilityRegistryApi.getBurnMintFactory(request);

  const { factoryId, choiceContext } = factoryWithContext;
  const { choiceContextData, disclosedContracts } = choiceContext;

  const values = (choiceContextData as any)?.values || {};

  const instrumentConfigCid = extractContractId(values, 'utility.digitalasset.com/instrument-configuration');

  if (!instrumentConfigCid) {
    throw new Error('Instrument configuration contract not found in choice context.');
  }

  const appRewardConfigCid = extractContractId(values, 'utility.digitalasset.com/app-reward-configuration');
  const featuredAppRightCid = extractContractId(values, 'utility.digitalasset.com/featured-app-right');

  const contextContractIds: ContextContractIds = {
    instrumentConfigurationCid: instrumentConfigCid as any,
    appRewardConfigurationCid: (appRewardConfigCid || null) as any,
    featuredAppRightCid: (featuredAppRightCid || null) as any,
  };

  return {
    factoryCid: factoryId as ContractId<BurnMintFactory>,
    contextContractIds,
    requiredDisclosedContracts: disclosedContracts.map(dc => ({
      contractId: dc.contractId as ContractId<AnyContract>,
      templateId: dc.templateId,
      createdEventBlob: dc.createdEventBlob,
    })),
  };
};

export default getContextContracts;
